(() => {
  const SECTION_SELECTOR = '[data-misc-usp-grid]';
  const ITEM_SELECTOR = '.js-misc-content-item';
  const VIDEO_SELECTOR = '.js-misc-content-video';
  const AUTOPLAY_DELAY = 5000;
  const instances = new WeakMap();

  class MiscContentUspGrid {
    constructor(section) {
      this.section = section;
      this.items = Array.from(section.querySelectorAll(ITEM_SELECTOR));
      this.videos = Array.from(section.querySelectorAll(VIDEO_SELECTOR));
      this.interval = null;
      this.observer = null;
      this.isInViewport = false;
      this.hasInteracted = false;
      this.activeTarget = Number(section.dataset.uspActive) || 1;
      this.enableAutoplay = section.dataset.enableAutoplay === 'true';

      if (!this.items.length) return;

      this.onMouseEnter = this.pauseAutoplay.bind(this);
      this.onIntersection = this.handleIntersection.bind(this);

      this.init();
    }

    init() {
      this.items.forEach((item) => {
        item.addEventListener('click', () => {
          this.hasInteracted = true;
          this.activate(Number(item.dataset.target), true);
        });

        item.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.hasInteracted = true;
          this.activate(Number(item.dataset.target), true);
        });
      });

      this.section.addEventListener('mouseenter', this.onMouseEnter);
      this.observer = new IntersectionObserver(this.onIntersection, {
        root: null,
        rootMargin: '0px'
      });
      this.observer.observe(this.section);
      this.activate(this.activeTarget, false);
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        this.isInViewport = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (this.hasInteracted) this.playActiveVideo();
          this.startAutoplay();
        } else {
          this.stopAutoplay();
          this.pauseVideos();
        }
      });
    }

    activate(target, pauseAutoplay) {
      if (!target) return;

      this.activeTarget = target;
      this.section.dataset.uspActive = String(target);

      this.items.forEach((item) => {
        const isActive = Number(item.dataset.target) === target;
        item.setAttribute('aria-pressed', String(isActive));
        item.classList.toggle('misc-content__image-wrap--active', isActive);
      });

      this.pauseVideos();
      if (this.isInViewport && this.hasInteracted) {
        this.playActiveVideo();
      }

      if (pauseAutoplay) {
        this.pauseAutoplay();
      }
    }

    playActiveVideo() {
      const activeItem = this.items.find((item) => Number(item.dataset.target) === this.activeTarget);
      const activeVideo = activeItem?.querySelector(VIDEO_SELECTOR);

      if (!activeVideo) return;

      const playPromise = activeVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    pauseVideos() {
      this.videos.forEach((video) => {
        video.pause();
      });
    }

    startAutoplay() {
      if (!this.enableAutoplay || this.interval || this.items.length < 2) return;

      this.interval = window.setInterval(() => {
        const nextTarget = this.activeTarget >= this.items.length ? 1 : this.activeTarget + 1;
        this.activate(nextTarget, false);
      }, AUTOPLAY_DELAY);
    }

    pauseAutoplay() {
      if (this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }
    }

    stopAutoplay() {
      this.pauseAutoplay();
    }

    destroy() {
      this.stopAutoplay();
      this.pauseVideos();
      this.section.removeEventListener('mouseenter', this.onMouseEnter);
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  function initSection(section) {
    if (!section || instances.has(section)) return;
    const instance = new MiscContentUspGrid(section);
    instances.set(section, instance);
  }

  function initSections(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  function destroySections(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      const instance = instances.get(section);
      if (!instance) return;
      instance.destroy();
      instances.delete(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections());
  } else {
    initSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySections(event.target);
  });
})();
